/**
 * Disease Hub API Client
 * Handles all Disease Hub related API calls with proper TypeScript types
 */

// API Types
export interface DiseaseCategory {
  id: string;
  name: string;
  description: string;
  disease_count: number;
  enabled: boolean;
}

export interface DiseaseHubDisease {
  id: string;
  name: string;
  description?: string;
  category: string;
  prevalence?: number;
  severity_score?: number;
  market_size?: string;
  development_stage: string;
  biomarkers: string[];
  associated_genes: string[];
}

export interface Biomarker {
  id: string;
  name: string;
  type: string;
  diseases: string[];
  validation_status: string;
  clinical_utility: string;
  evidence_level: string;
}

export interface DiseasePriorityRequest {
  category?: string;
  prevalence_min?: number;
  severity_min?: number;
  market_size?: string;
  biomarker_required?: boolean;
  development_stage?: string;
  prevalence_weight?: number;
  severity_weight?: number;
  unmet_need_weight?: number;
}

export interface BiomarkerAnalysisRequest {
  disease_id: string;
  biomarker_types?: string[];
  validation_status?: string;
}

export interface DiseaseAnalysisResult {
  disease: DiseaseHubDisease;
  priority_score: number;
  priority_rank: number;
  biomarker_count: number;
  gene_associations: number;
  development_feasibility: string;
  market_opportunity: string;
  rationale: string;
}

export interface BiomarkerAnalysisResult {
  biomarker: Biomarker;
  relevance_score: number;
  clinical_readiness: string;
  development_timeline: string;
  regulatory_pathway: string;
  evidence_summary: string;
}

export interface DiseaseHubStats {
  total_diseases: number;
  total_categories: number;
  total_biomarkers: number;
  diseases_by_category: Record<string, number>;
  market_distribution: Record<string, number>;
}

// API Client Class
class DiseaseHubClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}/api/disease-hub${endpoint}`;
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Disease Hub API Error: ${response.status} - ${errorText}`);
    }

    return response.json();
  }

  // Category endpoints
  async getCategories(): Promise<DiseaseCategory[]> {
    return this.request<DiseaseCategory[]>('/categories');
  }

  async getDiseasesByCategory(categoryId: string): Promise<DiseaseHubDisease[]> {
    return this.request<DiseaseHubDisease[]>(`/categories/${categoryId}/diseases`);
  }

  // Disease endpoints
  async getAllDiseases(filters?: { category?: string; search?: string }): Promise<DiseaseHubDisease[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.search) params.append('search', filters.search);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<DiseaseHubDisease[]>(`/diseases${query}`);
  }

  async getDisease(diseaseId: string): Promise<DiseaseHubDisease> {
    return this.request<DiseaseHubDisease>(`/diseases/${diseaseId}`);
  }

  // Disease prioritization
  async prioritizeDiseases(request: DiseasePriorityRequest): Promise<DiseaseAnalysisResult[]> {
    return this.request<DiseaseAnalysisResult[]>('/prioritize', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Biomarker endpoints
  async getBiomarkers(filters?: { disease_id?: string; biomarker_type?: string }): Promise<Biomarker[]> {
    const params = new URLSearchParams();
    if (filters?.disease_id) params.append('disease_id', filters.disease_id);
    if (filters?.biomarker_type) params.append('biomarker_type', filters.biomarker_type);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<Biomarker[]>(`/biomarkers${query}`);
  }

  async analyzeBiomarkers(request: BiomarkerAnalysisRequest): Promise<BiomarkerAnalysisResult[]> {
    return this.request<BiomarkerAnalysisResult[]>('/biomarker-analysis', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Statistics
  async getStats(): Promise<DiseaseHubStats> {
    return this.request<DiseaseHubStats>('/stats');
  }
}

// Export singleton instance
export const diseaseHubClient = new DiseaseHubClient();

// Export individual functions for easier use with React Query
export const diseaseHubApi = {
  getCategories: () => diseaseHubClient.getCategories(),
  getDiseasesByCategory: (categoryId: string) => diseaseHubClient.getDiseasesByCategory(categoryId),
  getAllDiseases: (filters?: { category?: string; search?: string }) => 
    diseaseHubClient.getAllDiseases(filters),
  getDisease: (diseaseId: string) => diseaseHubClient.getDisease(diseaseId),
  prioritizeDiseases: (request: DiseasePriorityRequest) => diseaseHubClient.prioritizeDiseases(request),
  getBiomarkers: (filters?: { disease_id?: string; biomarker_type?: string }) =>
    diseaseHubClient.getBiomarkers(filters),
  analyzeBiomarkers: (request: BiomarkerAnalysisRequest) =>
    diseaseHubClient.analyzeBiomarkers(request),
  getStats: () => diseaseHubClient.getStats(),
};

export default diseaseHubClient;